import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Sparkles, GraduationCap } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import Reveal from './Reveal.jsx';
import { subjects, STAGES, DURATIONS, price45, priceFor } from '../mock';
import { track } from '../lib/analytics';

function useAnimatedNumber(target) {
  const [val, setVal] = useState(target);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const from = val;
    const step = (now) => {
      const p = Math.min((now - start) / 500, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return val;
}

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`flex-1 min-w-0 h-11 px-2 rounded-xl border text-[13px] sm:text-sm font-semibold leading-tight whitespace-nowrap transition-colors ${active ? 'text-white' : 'bg-white text-slate-700 border-slate-200 hover:border-teal-300'}`}
    style={active ? { background: '#3d94b8', borderColor: '#3d94b8' } : {}}
  >
    {children}
  </button>
);

export default function PriceCalculator() {
  const [stage, setStage] = useState('mittel');
  const [subject, setSubject] = useState('Mathematik');
  const [lk, setLk] = useState(false);
  const [duration, setDuration] = useState(60);
  const [hours, setHours] = useState([4]);

  const stageSubjects = useMemo(() => subjects.filter((s) => s.stages.includes(stage)), [stage]);
  const current = stageSubjects.find((s) => s.name === subject) || stageSubjects[0];
  const canLK = stage === 'ober' && current?.lk;

  useEffect(() => { if (!stageSubjects.some((s) => s.name === subject)) setSubject(stageSubjects[0].name); }, [stage, stageSubjects, subject]);
  useEffect(() => { if (!canLK) setLk(false); }, [canLK]);

  const p45 = price45(current.name, stage, lk);
  const unit = priceFor(p45, duration);
  const total = unit * hours[0];
  const animated = useAnimatedNumber(total);
  const stageLabel = STAGES.find((s) => s.id === stage).label;

  const first = React.useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const t = setTimeout(() => track('price_calculated', { stage, subject: current.name, lk, duration, units: hours[0], unit_price: unit, monthly_total: total, currency: 'EUR' }), 800);
    return () => clearTimeout(t);
  }, [stage, current.name, lk, duration, hours, unit, total]);

  return (
    <Reveal>
      <div className="card-soft p-8 md:p-10" data-testid="price-calculator">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: '#e6f2f7' }}>
            <Calculator className="h-6 w-6" style={{ color: '#3d94b8' }} />
          </div>
          <div>
            <h3 className="text-2xl font-extrabold" style={{ color: '#1a3a5c' }}>Preisrechner</h3>
            <p className="text-sm text-slate-600">Stufe, Fach und Dauer wählen – dein Preis erscheint sofort.</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mt-8">
          <div className="space-y-6">
            <div>
              <label className="text-sm font-semibold text-slate-700">Stufe</label>
              <div className="mt-2 grid grid-cols-2 lg:grid-cols-4 gap-2">
                {STAGES.map((st) => <Chip key={st.id} testId={`calc-stage-${st.id}`} active={stage === st.id} onClick={() => setStage(st.id)}>{st.label}</Chip>)}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700">Fach</label>
              <Select value={current.name} onValueChange={setSubject}>
                <SelectTrigger data-testid="calc-subject-select" className="mt-2 rounded-xl h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {stageSubjects.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {stage === 'ober' && (
              <div>
                <label className="text-sm font-semibold text-slate-700">Kursart</label>
                <div className="mt-2 flex gap-2">
                  <Chip testId="calc-kurs-regular" active={!lk} onClick={() => setLk(false)}>Regulärer Kurs</Chip>
                  {canLK && (
                    <Chip testId="calc-kurs-lk" active={lk} onClick={() => setLk(true)}>
                      <span className="inline-flex items-center gap-1.5"><GraduationCap className="h-4 w-4" /> Leistungskurs</span>
                    </Chip>
                  )}
                </div>
                {!canLK && <p className="mt-2 text-xs text-slate-500">Leistungskurs-Nachhilfe gibt es für Mathematik, Physik, Englisch, Deutsch und Französisch.</p>}
              </div>
            )}
            <div>
              <label className="text-sm font-semibold text-slate-700">Dauer pro Einheit</label>
              <div className="mt-2 flex gap-2">
                {DURATIONS.map((d) => <Chip key={d} testId={`calc-duration-${d}`} active={duration === d} onClick={() => setDuration(d)}>{d} Min</Chip>)}
              </div>
            </div>
            <div>
              <div className="flex justify-between"><label className="text-sm font-semibold text-slate-700">Einheiten pro Monat</label><span className="text-sm font-bold" style={{ color: '#3d94b8' }}>{hours[0]}</span></div>
              <Slider data-testid="calc-hours-slider" value={hours} onValueChange={setHours} min={1} max={20} step={1} className="mt-4" />
            </div>
          </div>

          <div className="relative rounded-2xl p-8 flex flex-col justify-center shadow-xl text-white overflow-hidden" style={{ background: 'linear-gradient(135deg, #3d94b8 0%, #2d7d9a 100%)' }}>
            <div className="absolute -top-20 -right-20 h-52 w-52 rounded-full bg-white/10 blur-2xl" />
            <div className="text-sm opacity-90">Geschätzter Preis / Monat</div>
            <div data-testid="calc-total" className="mt-1 text-6xl font-extrabold tabular-nums leading-none">{animated} €</div>
            <div className="mt-3 text-sm opacity-90">= {hours[0]} × {unit} € ({duration} Min)</div>
            <div className="mt-5 pt-5 border-t border-white/20 space-y-2 text-sm">
              <div className="flex justify-between"><span>Fach</span><span className="font-semibold">{current.name}{lk ? ' (LK)' : ''}</span></div>
              <div className="flex justify-between"><span>Stufe</span><span className="font-semibold">{stageLabel}</span></div>
              <div className="flex justify-between"><span>Einheit</span><span data-testid="calc-unit" className="font-semibold">{unit} € / {duration} Min</span></div>
            </div>
            <Link to="/buchen" className="mt-5" data-track="price_calculator_book_click">
              <Button className="w-full bg-white hover:bg-slate-50 rounded-full h-12 font-semibold" style={{ color: '#2d7d9a' }}>
                <Sparkles className="mr-2 h-4 w-4" /> Jetzt Termin buchen
              </Button>
            </Link>
          </div>
        </div>

        <p className="mt-6 text-xs text-slate-500">Basis ist der 45-Minuten-Preis; 60 und 90 Minuten werden anteilig berechnet und auf volle Euro abgerundet. Keine Vertragsbindung, das Erstgespräch ist kostenlos.</p>
      </div>
    </Reveal>
  );
}
